'use client';

import {
  AE_PRICING_LINK_ABSOLUTE,
  ButtonLink,
  LinkWithArrow,
  READ_THE_SCIENCE_LINK_ACTIVITY_NUDGES,
  READ_THE_SCIENCE_LINK_FITNESS_INGIGHTS,
  READ_THE_SCIENCE_LINK_FUEL,
} from '../shared';
import { useProgressInViewport } from '../../hooks/scroll/useProgressInViewport';
import React, { useRef } from 'react';
import styles from './Steps.module.css';
import { TABLET_MAX_WIDTH_IN_PX } from '../theme';
import { desktopStepsAnim } from './desktopStepsAnim';
import { mobileStepsAnim } from './mobileStepsAnim';
import { useForwardSearchParams } from '../../hooks/navigation/useForwardSearchParams';

// images
import maskImageWithNotification from '../../assets/images/steps/mask_notification.png';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
const backgroundImageWithoutNotification = getCompressedAssetUrl(
  '/HomeAppSteps/background.png',
);
const backgroundImageRun = getCompressedAssetUrl(
  '/HomeAppSteps/step_section-background_run.png',
);
const backgroundImageLoseWeight = getCompressedAssetUrl(
  '/HomeAppSteps/step_section-background_loseWeight.png',
);
const backgroundImageMeditate = getCompressedAssetUrl(
  '/HomeAppSteps/step_section-background_meditate.png',
);
// number 1
const backgroundImageStepRun = getCompressedAssetUrl(
  '/m1/steps/Timeline_ss.png',
);
const notificationImageStepRun = getCompressedAssetUrl(
  '/m1/steps/walk_notification.png',
);
// number 2
const backgroundImageStepLoseWeight = getCompressedAssetUrl(
  '/m1/steps/Food_ss.png',
);
const notificationImageStepLoseWeight = getCompressedAssetUrl(
  '/m1/steps/health_notification.png',
);
// number 3
const backgroundImageStepMeditate = getCompressedAssetUrl(
  '/m1/steps/Fuel_ss.png',
);
const notificationImageStepMeditate = getCompressedAssetUrl(
  '/m1/steps/activity_notification.png',
);

const steps = {
  run: {
    img: backgroundImageStepRun,
    notification: notificationImageStepRun,
    backgroundImage: backgroundImageRun,
    hrefSecondary: READ_THE_SCIENCE_LINK_ACTIVITY_NUDGES,
    text: 'Get activity nudges, when your glucose levels rise',
  },
  loseWeight: {
    img: backgroundImageStepLoseWeight,
    notification: notificationImageStepLoseWeight,
    backgroundImage: backgroundImageLoseWeight,
    hrefSecondary: READ_THE_SCIENCE_LINK_FITNESS_INGIGHTS,
    text: 'Metabolic fitness insights delivered daily',
  },
  meditate: {
    img: backgroundImageStepMeditate,
    notification: notificationImageStepMeditate,
    hrefSecondary: READ_THE_SCIENCE_LINK_FUEL,
    text: 'Find your fuel',
    backgroundImage: backgroundImageMeditate,
  },
};

export const Steps = ({
  step,
  hideRequestAccess,
}: {
  step: keyof typeof steps;
  hideRequestAccess?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const notificationImgRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLDivElement | null>(null);
  const bgRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const deviceRef = useRef<HTMLDivElement | null>(null);
  const forwardSearchParams = useForwardSearchParams();

  useProgressInViewport({
    containerRef,
    startAtZero: false,
    callback(progress) {
      const refs = {
        imgRef,
        bgRef,
        notificationImgRef,
        contentRef,
        deviceRef,
      };
      if (window.innerWidth > TABLET_MAX_WIDTH_IN_PX) {
        return desktopStepsAnim(progress, refs);
      }
      return mobileStepsAnim(progress, refs);
    },
  });

  return (
    <div className={styles.container} ref={containerRef} id={`step-${step}`}>
      <div className={`${styles.subContainer} u-grid`}>
        <div
          className={styles.img}
          style={
            {
              ['--img']: `url('${backgroundImageWithoutNotification}')`,
            } as React.CSSProperties
          }
          ref={deviceRef}
        />
        <div
          className={styles.img}
          style={
            {
              ['--img']: `url('${steps[step].backgroundImage}')`,
            } as React.CSSProperties
          }
          ref={bgRef}
        />
        <div
          className={styles.blurredNotificationImg}
          style={
            {
              ['--notification-image']: `url('${steps[step].notification}')`,
              ['--mask-image']: `url('${maskImageWithNotification}')`,
            } as React.CSSProperties
          }
          ref={notificationImgRef}
        />
        <div
          className={styles.img}
          style={
            { ['--img']: `url('${steps[step].img}')` } as React.CSSProperties
          }
          ref={imgRef}
        />
        <div className={styles.content} ref={contentRef}>
          <h1 className={styles.title}>{steps[step].text}</h1>
          <div className={styles.buttons}>
            {!hideRequestAccess && (
              <ButtonLink
                className={styles.btnLink}
                href={forwardSearchParams(AE_PRICING_LINK_ABSOLUTE)}
                rel="noopener noreferrer"
                size="m"
              >
                Buy now
              </ButtonLink>
            )}
            <LinkWithArrow
              className={styles.linkArrow}
              href={steps[step].hrefSecondary}
              target="_blank"
              rel="noopener noreferrer"
            >
              Read the Science
            </LinkWithArrow>
          </div>
        </div>
      </div>
    </div>
  );
};
